// In Dashboard.js

import React, { useContext, useState, useEffect } from "react";
import { RoleContext } from "../reusableField/roleContext";
import { useLocation, useNavigate } from "react-router-dom";
import { Mail, UserMinus, UserPlus, MailOpen, ChevronRight } from "lucide-react";
import "./Dashboard.css";
import { Card } from "react-bootstrap";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { useQuery } from "@apollo/client";
import {
  CURRENT_USER_PROFIILE,
  GET_LEAD_STATS,
  GET_FLS_STATS,
} from "../services/userApi";
import LeadMetrics from "./LeadMetrics";
import BackButton from "../reusableField/BackButton";

const CircularProgress = ({ total, active, inactive }) => {
  const outerData = [{ name: "Total Leads", value: total, color: "#3B82F6" }];
  const innerData = [
    { name: "Active", value: active, color: "#22C55E" },
    { name: "Inactive", value: inactive, color: "#EF4444" },
  ];

  return (
    <div>
      <div className="relative">
        <div className="w-[220px] h-[220px] mx-auto">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={outerData}
                cx="50%"
                cy="50%"
                outerRadius={110}
                innerRadius={90}
                dataKey="value"
                strokeWidth={3}
                stroke="#ffffff"
                startAngle={90}
                endAngle={450}
              >
                {outerData.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
              <Pie
                data={innerData}
                cx="50%"
                cy="50%"
                outerRadius={80}
                innerRadius={60}
                dataKey="value"
                strokeWidth={3}
                stroke="#ffffff"
                startAngle={90}
                endAngle={450}
              >
                {innerData.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-800">
              {(((active ?? 0) / (total || 1)) * 100).toFixed(0)}%
            </div>
            <div className="text-sm text-gray-600">Assigned</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const location = useLocation();
  const { roleId: contextRoleId, setRoleId } = useContext(RoleContext);
  const locationRoleId = location.state?.roleId;
  const navigate = useNavigate();

  // Get roleId from multiple sources with priority
  const getEffectiveRoleId = () => {
    // Priority 1: location state
    if (locationRoleId) {
      sessionStorage.setItem('dashboardRoleId', locationRoleId);
      return locationRoleId;
    }
    // Priority 2: context
    if (contextRoleId) {
      sessionStorage.setItem('dashboardRoleId', contextRoleId);
      return contextRoleId;
    }
    // Priority 3: sessionStorage
    const storedRoleId = sessionStorage.getItem('dashboardRoleId');
    if (storedRoleId) {
      return storedRoleId;
    }
    return null;
  };

  const [effectiveRoleId, setEffectiveRoleId] = useState(getEffectiveRoleId());

  const { data: userProfileData, loading: userProfileLoading } = useQuery(
    CURRENT_USER_PROFIILE
  );

  const jobRoleId = userProfileData?.currentUserProfile?.roleId;

  // Update effectiveRoleId when any source changes
  useEffect(() => {
    const newRoleId = getEffectiveRoleId();
    if (newRoleId) {
      setEffectiveRoleId(newRoleId);
      setRoleId(newRoleId);
    } else if (jobRoleId) {
      // Fallback to jobRoleId from user profile
      setEffectiveRoleId(jobRoleId);
      setRoleId(jobRoleId);
      sessionStorage.setItem('dashboardRoleId', jobRoleId);
    }
  }, [locationRoleId, contextRoleId, jobRoleId, setRoleId]);

  // Final roleId to use in queries
  const finalRoleId = parseInt(effectiveRoleId || jobRoleId, 10);



  const { data: flsStatsData, loading: flsStatsLoading } = useQuery(
    GET_FLS_STATS,
    {
      variables: { roleId: finalRoleId },
      skip: !finalRoleId, // Skip query if roleId is not available
    }
  );

  const {
    data: leadStatsData,
    loading: leadStatsLoading,
    error: leadStatsError,
  } = useQuery(GET_LEAD_STATS, {
    variables: { jobroleId: finalRoleId },
    skip: !finalRoleId, // Skip query if roleId is not available
  });

  const handleStageClick = (stageId) => {
    navigate(`/leads`, {
      state: { stageId, roleId: finalRoleId },
    });
  };

  const handleStatusClick = (statusId) => {
    navigate(`/leads`, {
      state: { statusId, roleId: finalRoleId },
    });
  };

  // Show loading state while waiting for roleId
  if (!finalRoleId && (userProfileLoading || !userProfileData)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="dashboard">
        <div style={{ display: "flex", alignItems: "center", gap: "16px", marginTop: "55px", marginBottom: "16px" }}>
          <BackButton label="Back" />
          <h1 className="dashboard-title" style={{ margin: 0, marginTop: 0 }}>Dashboard</h1>
        </div>
        {/* <NavigationButtons showBack={true} showNext={false} /> */}

        <div className="dashboard-grid">
          <Card className="leads-card top-card">
            {leadStatsLoading ? (
              <div className="flex items-center justify-center p-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <CircularProgress
                total={leadStatsData?.getAssignedAndUnassignedLeadsCount?.total}
                active={leadStatsData?.getAssignedAndUnassignedLeadsCount?.assigned}
                inactive={leadStatsData?.getAssignedAndUnassignedLeadsCount?.unassigned}
              />
            )}

            {!leadStatsLoading && !leadStatsError && (
              <>
                <div className="total-leads">
                  <h2>{leadStatsData?.getAssignedAndUnassignedLeadsCount?.total}</h2>
                  <p>Total Leads</p>
                </div>
                <div className="leads-legend">
                  <div className="legend-item">
                    <div className="legend-dot assigned"></div>
                    <span>Assigned</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-dot unassigned"></div>
                    <span>Unassigned</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-dot total"></div>
                    <span>Total leads</span>
                  </div>
                </div>
              </>
            )}
          </Card>

          <div className="leads-status w-full">
            <div className="flex justify-between gap-4 w-full">
              <div className="w-1/2">
                <Card>
                  <div className="assigned-leads">
                    <div className="leads-info">
                      <Mail className="mail-icon assigned" />
                      Assigned Leads
                    </div>
                    <div className="leads-value">
                      <span style={{ color: "#22c55e" }}>
                        {leadStatsData?.getAssignedAndUnassignedLeadsCount?.assigned}
                      </span>
                    </div>
                  </div>
                </Card>
              </div>
              <div className="w-1/2">
                <Card>
                  <div className="unassigned-section">
                    <div className="leads-info">
                      <MailOpen className="mail-icon unassigned" />
                      Unassigned Leads
                    </div>
                    <div className="leads-value">
                      <span style={{ color: "#ef4444" }}>
                        {leadStatsData?.getAssignedAndUnassignedLeadsCount?.unassigned}
                      </span>
                    </div>
                  </div>
                </Card>
              </div>
            </div>

            {/* Use the reusable LeadMetrics component */}
            <LeadMetrics
              jobRoleId={finalRoleId}
              onStageClick={handleStageClick}
              onStatusClick={handleStatusClick}
              showStages={true}
              showStatus={true}
            />
          </div>
        </div>
      </div>

      <div className="dashboard_two flex w-full gap-6">
        <div className="fls-card w-3/5 bg-white rounded-lg shadow p-6">
          <div className="fls-info">
            <div>
              <div className="fls-title">
                USERS
                <ChevronRight className="arrow-icon inline ml-2" />
              </div>
              <div className="fls-count">
                {flsStatsLoading ? (
                  <div className="text-lg">Loading...</div>
                ) : (
                  flsStatsData?.getCountOfActiveInactiveUsers?.activeUsers +
                  flsStatsData?.getCountOfActiveInactiveUsers?.inactiveUsers
                )}
              </div>
              <div className="fls-total">Total Users</div>
            </div>

            <div className="fls-legend">
              <div className="legend-item">
                <div className="legend-line active"></div>
                <span>Active Users</span>
              </div>
              <div className="legend-item">
                <div className="legend-line inactive"></div>
                <span>Inactive Users</span>
              </div>
              <div className="legend-item">
                <div className="legend-line total"></div>
                <span>Total Users</span>
              </div>
            </div>
          </div>

          <div className="progress-container">
            <CircularProgress
              total={
                flsStatsData?.getCountOfActiveInactiveUsers?.activeUsers +
                flsStatsData?.getCountOfActiveInactiveUsers?.inactiveUsers
              }
              active={flsStatsData?.getCountOfActiveInactiveUsers?.activeUsers}
              inactive={flsStatsData?.getCountOfActiveInactiveUsers?.inactiveUsers}
            />
          </div>
        </div>

        <div className="fls-status w-2/5 bg-white rounded-lg shadow p-6">
          <Card>
            <div className="active-section">
              <div className="leads-info">
                <UserPlus className="user-icon active" />
                Active Users
              </div>
              <div className="leads-value">
                <span style={{ color: "#22c55e" }}>
                  {flsStatsData?.getCountOfActiveInactiveUsers?.activeUsers}
                </span>
              </div>
            </div>
          </Card>

          <Card>
            <div className="inactive-section">
              <div className="leads-info">
                <UserMinus className="user-icon inactive" />
                Inactive Users
              </div>
              <div className="leads-value">
                <span style={{ color: "#ef4444" }}>
                  {flsStatsData?.getCountOfActiveInactiveUsers?.inactiveUsers}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;